import { defineStore } from 'pinia';
import * as api from '@/api/http-api';
import type { CurrencyCode, GameConfig, BetDto } from '@/types/api';
import { parseError } from '@/utils/errorHandler';

interface GameState {
  // User session
  clientId: string;
  userId: string;
  userToken: string;
  username: string;
  currency: CurrencyCode;
  
  // Game state
  balance: number;
  gameConfig: GameConfig | null;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Bet history
  betHistory: BetDto[];
  historyPage: number;
  
  // Current game
  currentBet: BetDto | null;
}

export const useGameStore = defineStore('game', {
  state: (): GameState => ({
    clientId: '',
    userId: '',
    userToken: '',
    username: '',
    currency: 'DEMO' as CurrencyCode,
    
    balance: 0,
    gameConfig: null,
    isInitialized: false,
    isLoading: false,
    error: null,
    
    betHistory: [],
    historyPage: 1,
    
    currentBet: null
  }),
  
  getters: {
    minStake: (state) => Number(state.gameConfig?.minStake) ?? 10,
    maxStake: (state) => Number(state.gameConfig?.maxStake) ?? 1000,
    maxWin: (state) => Number(state.gameConfig?.maxWin) ?? 100000,
    canSpin: (state) => state.balance > 0 && !state.isLoading,
    formattedBalance: (state) => {
      return `${state.balance.toLocaleString()} ${state.currency}`;
    }
  },
  
  actions: {
    async initGame(params: { 
      clientId: string; 
      userId: string; 
      userToken: string; 
      username: string; 
      currency: CurrencyCode;
    }) {
      this.isLoading = true;
      this.error = null;
      
      try {
        // Store user params
        this.clientId = params.clientId;
        this.userId = params.userId;
        this.userToken = params.userToken;
        this.username = params.username;
        this.currency = params.currency;
        
        // Call launch API
        const response = await api.launch({
          clientId: this.clientId,
          userId: this.userId,
          userToken: this.userToken,
          username: this.username,
          currency: this.currency
        });
        
        console.log('API response gameConfig:', response.gameConfig);
        console.log('minStake from API:', response.gameConfig.minStake, 'type:', typeof response.gameConfig.minStake);
        console.log('maxStake from API:', response.gameConfig.maxStake, 'type:', typeof response.gameConfig.maxStake);
        
        // Store game data - ensure numeric values
        this.balance = Number(response.balance);
        this.gameConfig = {
          minStake: Number(response.gameConfig.minStake),
          maxStake: Number(response.gameConfig.maxStake),
          maxWin: Number(response.gameConfig.maxWin)
        };
        this.betHistory = response.bets || [];
        this.isInitialized = true;
        
        console.log('Game initialized successfully', {
          balance: this.balance,
          currency: this.currency,
          config: this.gameConfig
        });
        
      } catch (error) {
        this.error = parseError(error);
        console.error('Game initialization failed:', error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    
    async placeBet(stake: number): Promise<BetDto> {
      // Validation
      if (!this.gameConfig) {
        throw new Error('Game not initialized');
      }
      
      if (stake < this.gameConfig.minStake || stake > this.gameConfig.maxStake) {
        throw new Error(`Stake must be between ${this.gameConfig.minStake} and ${this.gameConfig.maxStake}`);
      }
      
      if (this.balance < stake) {
        throw new Error('Insufficient balance');
      }
      
      this.isLoading = true;
      this.error = null;
      
      try {
        const response = await api.placeBet({
          clientId: this.clientId,
          userId: this.userId,
          userToken: this.userToken,
          username: this.username,
          currency: this.currency,
          stake: stake,
          picks: [] // Slots don't use picks
        });
        
        // Update state
        this.currentBet = response.bet;
        this.balance = response.balance;
        
        // Add to history (at beginning)
        if (this.betHistory.length === 0 || this.betHistory[0].id !== response.bet.id) {
          this.betHistory.unshift(response.bet);
          console.log('Added bet to history, total bets:', this.betHistory.length);
          console.log('Latest bet:', response.bet);
        }
        
        // Keep only last 50 bets in memory
        if (this.betHistory.length > 50) {
          this.betHistory = this.betHistory.slice(0, 50);
        }
        
        console.log('Bet placed successfully', {
          reels: response.bet.reels,
          winAmount: response.bet.netWinning,
          balance: response.balance
        });
        
        return response.bet;
        
      } catch (error) {
        this.error = parseError(error);
        console.error('Place bet failed:', error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    
    async loadBetHistory(page: number = 1, limit: number = 10): Promise<void> {
      try {
        const bets = await api.fetchBetHistory({
          clientId: this.clientId,
          userId: this.userId,
          userToken: this.userToken,
          page,
          limit
        });
        
        this.betHistory = bets;
        this.historyPage = page;
        
      } catch (error) {
        this.error = parseError(error);
        console.error('Failed to load bet history:', error);
        throw error;
      }
    },
    
    clearError(): void {
      this.error = null;
    },
    
    getReelsForCurrentBet(): number[][] {
      return this.currentBet?.reels ?? [];
    },
    
    getWinningLines(): number[] {
      return this.currentBet?.winningLines ?? [];
    },
    
    getWinningSymbols(): number[] {
      return this.currentBet?.winningSymbols ?? [];
    },
    
    getLinePayouts(): { lineIndex: number; symbol: number; count: number; payout: number }[] {
      return this.currentBet?.linePayouts ?? [];
    }
  }
});